import React, { useEffect, useMemo, useState } from 'react';
import {
  Accordion,
  Badge,
  Center,
  Group,
  Loader,
  SimpleGrid,
  Stack,
  Table,
  Text,
} from '@mantine/core';
import { getAccentColor } from '../../lib/jlfcTheme';
import JlfcModal from '../JlfcModal';
import { CancelButton } from '../FormActionButtons';
import ElectriqueIcon from '../ElectriqueIcon';
import { authFetch, parseJsonResponse } from '../../lib/api';
import { isElectrique } from '../../lib/electrique';

function formatMoney(v) {
  if (v == null || v === '') return '—';
  const n = Number(v);
  if (!Number.isFinite(n)) return '—';
  return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(n);
}

function formatDate(v) {
  if (!v) return '—';
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('fr-FR');
}

function DetailField({ label, value, span = 1 }) {
  return (
    <Stack gap={2} style={{ gridColumn: span > 1 ? `span ${span}` : undefined }}>
      <Text size="xs" fw={600} c="dimmed" tt="uppercase">
        {label}
      </Text>
      <Text size="sm">{value ?? '—'}</Text>
    </Stack>
  );
}

function enginLabelFromParts(marque, modele, serie) {
  return [marque, modele, serie].filter(Boolean).join(' · ') || '—';
}

const ENDPOINTS = {
  controle: (id) => `/api/client-portal/controles/${id}`,
  facture: (id) => `/api/client-portal/factures/${id}`,
  engin: (id) => `/api/client-portal/engins/${id}`,
  reglement: (id) => `/api/client-portal/reglements/${id}`,
};

const TITLES = {
  controle: 'Détail du contrôle',
  facture: 'Détail de la facture',
  engin: 'Détail de l\'engin',
  reglement: 'Détail du règlement',
};

function ControleBody({ data }) {
  return (
    <Stack gap="md">
      <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
        <DetailField
          label="Engin"
          value={
            <Group gap={6} wrap="nowrap">
              <span>{data.engin_label || enginLabelFromParts(data.engin_marque, data.engin_modele, data.engin_num_serie)}</span>
              {isElectrique(data.electrique || data.engin_electrique) ? <ElectriqueIcon /> : null}
            </Group>
          }
        />
        <DetailField label="Facture" value={data.num_facture} />
        <DetailField label="Date contrôle" value={formatDate(data.date_controle)} />
        <DetailField label="Fin validité" value={formatDate(data.date_fin_validite)} />
        <DetailField label="Coût" value={formatMoney(data.cout)} />
        <DetailField label="Relance" value={data.relance ? 'Oui' : 'Non'} />
        {data.lieu ? <DetailField label="Lieu" value={data.lieu} span={2} /> : null}
        {data.commentaire ? (
          <DetailField label="Commentaire" value={data.commentaire} span={2} />
        ) : null}
      </SimpleGrid>

      {data.accessoires?.length > 0 ? (
        <Stack gap="xs">
          <Text size="xs" fw={600} c="dimmed" tt="uppercase">
            Accessoires contrôlés
          </Text>
          <Table.ScrollContainer minWidth={400}>
            <Table striped withTableBorder withColumnBorders size="sm">
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>N° série</Table.Th>
                  <Table.Th>Marque</Table.Th>
                  <Table.Th>Modèle</Table.Th>
                  <Table.Th>Année</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {data.accessoires.map((a) => (
                  <Table.Tr key={a.id}>
                    <Table.Td>{a.num_serie || '—'}</Table.Td>
                    <Table.Td>{a.marque_nom || '—'}</Table.Td>
                    <Table.Td>{a.modele_nom || '—'}</Table.Td>
                    <Table.Td>{a.annee_mec || '—'}</Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </Table.ScrollContainer>
        </Stack>
      ) : null}
    </Stack>
  );
}

function FactureBody({ data }) {
  return (
    <Stack gap="md">
      <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
        <DetailField label="N° facture" value={data.num_facture} />
        <DetailField label="Date émission" value={formatDate(data.date_emission)} />
        <DetailField label="Montant HT" value={formatMoney(data.montant_du_ht)} />
        <DetailField label="TVA" value={formatMoney(data.montant_tva)} />
        <DetailField label="Montant TTC" value={formatMoney(data.montant_du_ttc)} />
        <DetailField label="Restant dû" value={formatMoney(data.restant_du)} />
        <DetailField
          label="Statut"
          value={
            data.solde ? (
              <Badge color="teal" variant="light">
                Soldée
              </Badge>
            ) : (
              <Badge color="orange" variant="light">
                En cours
              </Badge>
            )
          }
        />
      </SimpleGrid>

      {data.controles?.length > 0 ? (
        <Stack gap="xs">
          <Text size="xs" fw={600} c="dimmed" tt="uppercase">
            Contrôles facturés
          </Text>
          <Table striped withTableBorder size="sm">
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Date</Table.Th>
                <Table.Th>Engin</Table.Th>
                <Table.Th>Coût</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {data.controles.map((c) => (
                <Table.Tr key={c.id}>
                  <Table.Td>{formatDate(c.date_controle)}</Table.Td>
                  <Table.Td>
                    {c.engin_label ||
                      enginLabelFromParts(c.engin_marque, c.engin_modele, c.engin_num_serie)}
                  </Table.Td>
                  <Table.Td>{formatMoney(c.cout)}</Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </Stack>
      ) : null}

      {data.reglements?.length > 0 ? (
        <Stack gap="xs">
          <Text size="xs" fw={600} c="dimmed" tt="uppercase">
            Règlements affectés
          </Text>
          <Table striped withTableBorder size="sm">
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Date</Table.Th>
                <Table.Th>Mode</Table.Th>
                <Table.Th>Montant</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {data.reglements.map((r) => (
                <Table.Tr key={r.id}>
                  <Table.Td>{formatDate(r.date_reglement)}</Table.Td>
                  <Table.Td>{r.mode_label || '—'}</Table.Td>
                  <Table.Td>{formatMoney(r.montant)}</Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </Stack>
      ) : null}
    </Stack>
  );
}

function EnginBody({ data }) {
  return (
    <Stack gap="md">
      <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
        <DetailField label="Marque" value={data.marque_nom} />
        <DetailField label="Modèle" value={data.modele_nom} />
        <DetailField label="Type" value={data.type_nom} />
        <DetailField label="N° série" value={data.num_serie} />
        <DetailField label="N° parc" value={data.num_parc} />
        <DetailField label="Autre réf." value={data.autre_ref} />
        <DetailField label="Année MEC" value={data.annee_mec || data.annee_creation} />
        <DetailField
          label="Énergie"
          value={data.electrique ? 'Électrique' : 'Thermique'}
        />
        <DetailField label="Statut" value={data.actif ? 'Actif' : 'Inactif'} />
      </SimpleGrid>

      {data.controles?.length > 0 ? (
        <Stack gap="xs">
          <Text size="xs" fw={600} c="dimmed" tt="uppercase">
            Historique des contrôles
          </Text>
          <Table striped withTableBorder size="sm">
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Date contrôle</Table.Th>
                <Table.Th>Fin validité</Table.Th>
                <Table.Th>Relance</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {data.controles.map((c) => (
                <Table.Tr key={c.id}>
                  <Table.Td>{formatDate(c.date_controle)}</Table.Td>
                  <Table.Td>{formatDate(c.date_fin_validite)}</Table.Td>
                  <Table.Td>{c.relance ? 'Oui' : 'Non'}</Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </Stack>
      ) : null}
    </Stack>
  );
}

function ReglementBody({ data }) {
  return (
    <Stack gap="md">
      <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
        <DetailField label="Date" value={formatDate(data.date_reglement)} />
        <DetailField label="Montant" value={formatMoney(data.montant)} />
        <DetailField label="Mode" value={data.mode_label} />
        <DetailField label="Référence" value={data.ref_reglement} />
      </SimpleGrid>

      {data.factures?.length > 0 ? (
        <Accordion variant="separated">
          {data.factures.map((f) => (
            <Accordion.Item key={f.id} value={String(f.id)}>
              <Accordion.Control>
                <Group justify="space-between" wrap="nowrap" pr="md">
                  <Text size="sm" fw={600}>
                    {f.num_facture || `Facture #${f.id}`}
                  </Text>
                  <Text size="sm" c="dimmed">
                    {formatDate(f.date_emission)} · {formatMoney(f.montant_du_ttc)}
                  </Text>
                </Group>
              </Accordion.Control>
              <Accordion.Panel>
                {f.controles?.length ? (
                  <Table striped withTableBorder size="sm">
                    <Table.Thead>
                      <Table.Tr>
                        <Table.Th>Date</Table.Th>
                        <Table.Th>Engin</Table.Th>
                      </Table.Tr>
                    </Table.Thead>
                    <Table.Tbody>
                      {f.controles.map((c) => (
                        <Table.Tr key={c.id}>
                          <Table.Td>{formatDate(c.date_controle)}</Table.Td>
                          <Table.Td>
                            {c.engin_label ||
                              enginLabelFromParts(
                                c.engin_marque,
                                c.engin_modele,
                                c.engin_num_serie,
                              )}
                          </Table.Td>
                        </Table.Tr>
                      ))}
                    </Table.Tbody>
                  </Table>
                ) : (
                  <Text size="sm" c="dimmed">
                    Aucun contrôle lié.
                  </Text>
                )}
              </Accordion.Panel>
            </Accordion.Item>
          ))}
        </Accordion>
      ) : null}
    </Stack>
  );
}

export default function ClientPortalDetailModal({ opened, onClose, type, recordId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const endpoint = useMemo(() => {
    if (!type || !recordId || !ENDPOINTS[type]) return null;
    return ENDPOINTS[type](recordId);
  }, [type, recordId]);

  useEffect(() => {
    if (!opened || !endpoint) {
      setData(null);
      setError(null);
      return undefined;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);

    authFetch(endpoint)
      .then((r) => parseJsonResponse(r))
      .then((d) => {
        if (cancelled) return;
        if (d.success) setData(d.data);
        else setError(d.message || 'Introuvable');
      })
      .catch(() => {
        if (!cancelled) setError('Chargement impossible');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [opened, endpoint]);

  const title = useMemo(() => {
    if (!type) return '';
    const base = TITLES[type] || 'Détail';
    if (type === 'facture' && data?.num_facture) return `Facture ${data.num_facture}`;
    if (type === 'reglement' && data?.ref_reglement) return `Règlement ${data.ref_reglement}`;
    if (data?.id) return `${base} #${data.id}`;
    return base;
  }, [type, data]);

  function renderBody() {
    if (!data) return null;
    switch (type) {
      case 'controle':
        return <ControleBody data={data} />;
      case 'facture':
        return <FactureBody data={data} />;
      case 'engin':
        return <EnginBody data={data} />;
      case 'reglement':
        return <ReglementBody data={data} />;
      default:
        return null;
    }
  }

  return (
    <JlfcModal opened={opened} onClose={onClose} title={title} size="lg">
      {loading ? (
        <Center py="xl">
          <Loader color={getAccentColor()} />
        </Center>
      ) : error ? (
        <Text c="red" size="sm">
          {error}
        </Text>
      ) : (
        renderBody()
      )}
      <Group justify="flex-end" mt="md">
        <CancelButton onClick={onClose}>Fermer</CancelButton>
      </Group>
    </JlfcModal>
  );
}
